'use client';

import React from 'react';
import { Printer } from 'lucide-react';
import type { GuideContent } from '../../data/guides/types';

interface GuidePrintLayoutProps {
  guide: GuideContent;
}

export default function GuidePrintLayout({ guide }: GuidePrintLayoutProps) {
  const {
    meta,
    welcome,
    audience,
    modules,
    faq,
    plan,
    planNote,
    cheatSheet,
    prompts,
    mentalModel,
    platformComparison,
    healthSection,
    crisisResources,
    aftercare,
    closingNote,
  } = guide;

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <div className="guide-print-toolbar fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b border-white/10 bg-charcoal-950/95 px-4 py-3 backdrop-blur-md print:hidden">
        <p className="text-sm font-medium text-pink-200/80">{meta.title}</p>
        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-pink-500 to-purple-700 px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
        >
          <Printer className="h-4 w-4" aria-hidden />
          Save as PDF
        </button>
      </div>

      <div className="guide-print-root mx-auto max-w-[816px] bg-white px-8 py-12 pt-20 text-charcoal-900 print:max-w-none print:px-12 print:py-0 print:pt-0">
        <section className="guide-print-page mb-8 flex min-h-[1000px] flex-col justify-between border-b-4 border-pink-500 pb-12 print:min-h-0 print:break-after-page print:pb-0">
          <div>
            <p className="mb-6 text-xs font-bold tracking-[0.2em] text-pink-600">{meta.eyebrow}</p>
            <h1 className="mb-4 text-4xl font-bold leading-tight text-charcoal-950 md:text-5xl">{meta.title}</h1>
            <p className="mb-10 max-w-lg text-lg leading-relaxed text-charcoal-600">{meta.subtitle}</p>
          </div>

          <div className="grid grid-cols-4 gap-4 border-t border-charcoal-200 pt-8">
            {[
              { label: 'Format', value: meta.format },
              { label: 'Duration', value: meta.duration },
              { label: 'Level', value: meta.level },
              { label: 'Updated', value: meta.updated },
            ].map((item) => (
              <div key={item.label}>
                <p className="text-xs font-semibold uppercase tracking-wider text-charcoal-400">{item.label}</p>
                <p className="text-sm font-bold text-charcoal-800">{item.value}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-sm text-charcoal-500">By the experts at HeavenGirlfriend.com</p>
        </section>

        <section className="guide-print-page mb-8 print:break-after-page">
          <h2 className="mb-6 text-2xl font-bold text-charcoal-950">{welcome.headline}</h2>
          <div className="space-y-4 text-sm leading-relaxed text-charcoal-700">
            {welcome.body.map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
          </div>
          <p className="mt-6 text-sm font-medium text-pink-600">— {welcome.signoff}</p>
        </section>

        {mentalModel && (
          <section className="guide-print-page mb-8 print:break-after-page">
            <h2 className="mb-6 text-2xl font-bold text-charcoal-950">The Mental Model</h2>
            <div className="space-y-4">
              {mentalModel.forces.map((force) => (
                <div key={force.number}>
                  <h3 className="font-bold text-charcoal-800">
                    {force.number} {force.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-charcoal-700">{force.body}</p>
                </div>
              ))}
            </div>
            <blockquote className="mt-4 border-l-4 border-pink-500 bg-pink-50 px-4 py-3 text-sm italic text-charcoal-700">
              {mentalModel.oneSentence}
            </blockquote>
          </section>
        )}

        <section className="guide-print-page mb-8 print:break-after-page">
          <h2 className="mb-6 text-2xl font-bold text-charcoal-950">Who This Guide Is For</h2>
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <h3 className="mb-4 font-bold text-charcoal-800">This is for you if:</h3>
              <ul className="space-y-2 text-sm text-charcoal-700">
                {audience.forYou.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="text-pink-500">✓</span> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="mb-4 font-bold text-charcoal-800">This is NOT for you if:</h3>
              <ul className="space-y-2 text-sm text-charcoal-700">
                {audience.notForYou.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="text-charcoal-400">✗</span> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {modules.map((mod) => (
          <section key={mod.number} className="guide-print-page mb-8 print:break-after-page">
            <p className="mb-1 text-xs font-bold tracking-[0.15em] text-pink-600">{mod.label}</p>
            <h2 className="mb-2 text-2xl font-bold text-charcoal-950">{mod.title}</h2>
            <p className="mb-6 text-xs text-charcoal-500">{mod.duration}</p>

            <div className="mb-6 space-y-3 text-sm leading-relaxed text-charcoal-700">
              {mod.body.map((p) => (
                <p key={p.slice(0, 40)}>{p}</p>
              ))}
            </div>

            {mod.insight && (
              <blockquote className="mb-6 border-l-4 border-pink-500 bg-pink-50 px-4 py-3 text-sm italic text-charcoal-700">
                &ldquo;{mod.insight}&rdquo;
              </blockquote>
            )}

            <h3 className="mb-3 text-sm font-bold text-charcoal-800">You&apos;ll walk away knowing how to:</h3>
            <ul className="space-y-1.5 text-sm text-charcoal-700">
              {mod.takeaways.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="text-pink-500">✓</span> {item}
                </li>
              ))}
            </ul>
          </section>
        ))}

        {platformComparison && platformComparison.length > 0 && (
          <section className="guide-print-page mb-8 print:break-after-page">
            <h2 className="mb-6 text-2xl font-bold text-charcoal-950">Platform Comparison</h2>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-charcoal-200">
                  <th className="px-2 py-2 text-left font-bold text-charcoal-800">Platform</th>
                  <th className="px-2 py-2 text-left font-bold text-charcoal-800">Strongest at</th>
                  <th className="px-2 py-2 text-left font-bold text-charcoal-800">Watch out for</th>
                </tr>
              </thead>
              <tbody>
                {platformComparison.map((row) => (
                  <tr key={row.platform} className="border-b border-charcoal-100">
                    <td className="px-2 py-2 font-semibold text-charcoal-800">{row.platform}</td>
                    <td className="px-2 py-2 text-charcoal-700">{row.strongest}</td>
                    <td className="px-2 py-2 text-charcoal-700">{row.watchOut}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {prompts && prompts.length > 0 && (
          <section className="guide-print-page mb-8 print:break-after-page">
            <h2 className="mb-6 text-2xl font-bold text-charcoal-950">{prompts.length} Copy-Paste Prompts</h2>
            <div className="space-y-6">
              {prompts.map((prompt) => (
                <div key={prompt.label} className="rounded-lg border border-charcoal-200 p-5">
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-pink-600">{prompt.category}</p>
                  <h3 className="mb-3 font-bold text-charcoal-800">{prompt.label}</h3>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-charcoal-700">{prompt.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {plan && plan.length > 0 && (
          <section className="guide-print-page mb-8 print:break-after-page">
            <h2 className="mb-6 text-2xl font-bold text-charcoal-950">Your First 30 Days</h2>
            <div className="space-y-6">
              {plan.map((week) => (
                <div key={week.week} className="rounded-lg border border-charcoal-200 p-5">
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-pink-600">{week.week}</p>
                  <h3 className="mb-2 font-bold text-charcoal-800">{week.title}</h3>
                  <p className="text-sm leading-relaxed text-charcoal-700">{week.body}</p>
                </div>
              ))}
            </div>
            {planNote && <p className="mt-6 text-sm italic text-charcoal-600">{planNote}</p>}
          </section>
        )}

        {healthSection && (
          <section className="guide-print-page mb-8 print:break-after-page">
            <h2 className="mb-4 text-2xl font-bold text-charcoal-950">{healthSection.title}</h2>
            <p className="mb-6 text-sm leading-relaxed text-charcoal-700">{healthSection.intro}</p>
            <div className="space-y-4">
              {healthSection.habits.map((habit) => (
                <div key={habit.title}>
                  <h3 className="font-bold text-charcoal-800">{habit.title}</h3>
                  <p className="text-sm text-charcoal-700">{habit.body}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {aftercare && (
          <section className="guide-print-page mb-8 print:break-after-page">
            <h2 className="mb-4 text-2xl font-bold text-charcoal-950">{aftercare.title}</h2>
            <p className="text-sm leading-relaxed text-charcoal-700">{aftercare.body}</p>
          </section>
        )}

        {crisisResources && (
          <section className="guide-print-page mb-8 print:break-after-page">
            <h2 className="mb-4 text-2xl font-bold text-charcoal-950">{crisisResources.title}</h2>
            <div className="space-y-3">
              {crisisResources.body.map((p) => (
                <p key={p.slice(0, 40)} className="text-sm leading-relaxed text-charcoal-700">
                  {p}
                </p>
              ))}
            </div>
          </section>
        )}

        {cheatSheet && cheatSheet.length > 0 && (
          <section className="guide-print-page mb-8 print:break-after-page">
            <h2 className="mb-6 text-2xl font-bold text-charcoal-950">The Whole Guide, One Page</h2>
            <ol className="space-y-2 text-sm text-charcoal-700">
              {cheatSheet.map((line, i) => (
                <li key={line} className="flex gap-3">
                  <span className="font-bold text-pink-600">{String(i + 1).padStart(2, '0')}</span>
                  {line}
                </li>
              ))}
            </ol>
          </section>
        )}

        <section className="guide-print-page mb-8 print:break-after-page">
          <h2 className="mb-6 text-2xl font-bold text-charcoal-950">FAQ</h2>
          <div className="space-y-6">
            {faq.map((item) => (
              <div key={item.question}>
                <h3 className="mb-2 font-bold text-charcoal-800">{item.question}</h3>
                <p className="text-sm leading-relaxed text-charcoal-700">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="guide-print-page border-t-4 border-pink-500 pt-8">
          {closingNote && (
            <p className="mb-6 text-sm leading-relaxed text-charcoal-700">{closingNote}</p>
          )}
          <h2 className="mb-4 text-2xl font-bold text-charcoal-950">Ready to go deeper?</h2>
          <p className="mb-6 text-sm leading-relaxed text-charcoal-700">
            Explore more free guides at Heaven Academy, or find the platform built to remember you.
          </p>
          <p className="mb-2 text-sm font-bold text-pink-600">heavengirlfriend.com/courses</p>
          <p className="mb-8 text-sm font-bold text-pink-600">heavengirlfriend.com/best-ai-girlfriend</p>
          <p className="text-xs leading-relaxed text-charcoal-500">
            Heaven Girlfriend is an independent review site. Some links are affiliate links. AI companions
            are simulated experiences, not substitutes for human relationships or professional mental health
            support.
          </p>
        </section>
      </div>
    </>
  );
}
